import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import Button from "../../components/button/Button";
import Input from "../../components/form-item/Input";
import SvgLoadingAnimation from "../../components/loading/index";
import FlowingCircles from "../index/components/FlowingCircles";
import InviteRegistrationModal from "./components/InviteRegistrationModal";
import OfferOnboardingWizard from "./components/OfferOnboardingWizard";

import {
  useVerifyInvitationTokenMutation,
  useAcceptInvitationMutation,
  useResendSelfInvitationMutation,
} from "../../app/services/invitations";
// @ts-expect-error - JSファイルの型定義なし
import { useSigninUserMutation, userApi } from "../../app/accounts/authuser";
import {
  setAuthTokenToCookies,
  removeAuthTokenFromCookies,
} from "../../common/utils";
import { extractErrorMessage, isExpiredInviteError } from "./inviteUtils";

type InviteStatus =
  | "loading"
  | "existing_user_logged_in"
  | "existing_user_not_logged_in"
  | "new_user"
  | "already_accepted"
  | "accepting"
  | "error"
  | "done";

// ===== Styled Components =====

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

const Card = styled.div`
  position: relative;
  z-index: 10;
  width: clamp(320px, 38.2vw, 420px);
  border: 1px solid var(--soremo-border);
  border-radius: 12px;
  padding: 32px 16px;

  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const StatusMessage = styled.div`
  text-align: center;
  padding: 24px 0;
`;

const ErrorMessage = styled.div.attrs({
  className: "bodytext-13",
})`
  color: var(--soremo-green);
  padding: 8px 12px;
  background-color: var(--soremo-bg-white);
  border: 1px solid var(--soremo-green);
  border-radius: 4px;
`;

// ===== Component =====

const Invite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [verifyToken] = useVerifyInvitationTokenMutation();
  const [acceptInvitation] = useAcceptInvitationMutation();
  const [resendSelfInvitation] = useResendSelfInvitationMutation();
  const [loginUser] = useSigninUserMutation();

  const [status, setStatus] = useState<InviteStatus>("loading");
  const [projectName, setProjectName] = useState<string>("");
  const [projectId, setProjectId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [loginUsername, setLoginUsername] = useState<string>("");
  const [loginPassword, setLoginPassword] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loginError, setLoginError] = useState<string>("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // 期限切れのときだけ「新しいリンクをメールで送る」導線を出す。
  const [isExpired, setIsExpired] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string>("");
  const [inviteType, setInviteType] = useState<"project" | "offer">("project");
  const [offerId, setOfferId] = useState<string>("");
  const [senderName, setSenderName] = useState<string>("");
  // オファー概要（書類確認ステップで契約書 PDF 生成に失敗したときの
  // フォールバック表示に使う、ロール / 報酬 / 納期の控え）。
  const [offerRole, setOfferRole] = useState<string>("");
  const [offerReward, setOfferReward] = useState<number | null>(null);
  const [offerDeadline, setOfferDeadline] = useState<string>("");

  // オファー招待のときはオファースレッドへ、それ以外はプロジェクトトップへ遷移する。
  const buildDestination = useCallback(
    (projectId: string, offerIdOverride?: string): string => {
      const targetOfferId = offerIdOverride || offerId;
      if (inviteType === "offer" && targetOfferId) {
        return `/${projectId}?tab=dm&thread=${targetOfferId}`;
      }
      return `/${projectId}`;
    },
    [inviteType, offerId]
  );

  // トークン検証
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("招待リンクが無効です。");
      return;
    }

    const verify = async () => {
      try {
        const result = await verifyToken({ token }).unwrap();
        setProjectName(result.project_name || "");
        setProjectId(result.project_id || "");
        setEmail(result.email || "");
        setLoginUsername(result.email || "");
        setInviteType(result.invite_type === "offer" ? "offer" : "project");
        setOfferId(result.offer_id || "");
        setSenderName(result.sender_name || "");
        setOfferRole(result.offer_summary || result.offer_role || "");
        setOfferReward(
          result.offer_reward === undefined ? null : result.offer_reward,
        );
        setOfferDeadline(result.offer_deadline || "");

        // 「サインインし直し」が必要な動線に入る場合は、旧ユーザーの auth_token / RTK Query
        // キャッシュを破棄してから描画する（他ユーザーの /api/me 結果が UI に漏れないように）。
        // サーバ側セッションは BE の TokenAuthentication 優先で構造的に無視される。
        if (
          result.status === "new_user" ||
          result.status === "existing_user_not_logged_in"
        ) {
          removeAuthTokenFromCookies();
          dispatch(userApi.util.resetApiState());
        }

        setStatus(result.status);
      } catch (err) {
        setStatus("error");
        setIsExpired(isExpiredInviteError(err));
        setErrorMessage(extractErrorMessage(err, "招待リンクが無効または期限切れです。"));
      }
    };

    verify();
  }, [token, verifyToken, dispatch]);

  // 期限切れリンクから、同じ宛先に新しいリンクを送り直す。
  const handleResend = useCallback(async () => {
    if (!token || isResending) return;

    setIsResending(true);
    setResendMessage("");
    try {
      const result = await resendSelfInvitation({ token }).unwrap();
      setIsExpired(false);
      // 期限切れの間に登録が済んでいた場合は、再送ではなくサインインへ案内する。
      if (result.status === "already_accepted") {
        setResendMessage(
          result.message ||
            "この招待はすでに承認されています。サインインしてご確認ください。",
        );
        return;
      }
      setResendMessage(
        result.email
          ? `${result.email} 宛に新しい招待リンクを送信しました。メールをご確認ください。`
          : "新しい招待リンクをメールで送信しました。",
      );
    } catch (err) {
      setResendMessage(
        extractErrorMessage(err, "招待リンクの再送に失敗しました。"),
      );
    } finally {
      setIsResending(false);
    }
  }, [token, isResending, resendSelfInvitation]);

  // ログイン済み既存ユーザー -> 自動承認
  useEffect(() => {
    if (status !== "existing_user_logged_in" || !token) return;

    const accept = async () => {
      setStatus("accepting");
      try {
        const result = await acceptInvitation({ token }).unwrap();
        navigate(buildDestination(result.project_id, result.offer_id), { replace: true });
      } catch (err) {
        setStatus("error");
        setErrorMessage(extractErrorMessage(err, "プロジェクトへの参加に失敗しました。"));
      }
    };

    accept();
  }, [status, token, acceptInvitation, navigate, buildDestination]);

  // サインインフォーム送信 -> ログイン後に自動承認
  const handleLogin = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!token) return;

      setLoginError("");
      setIsLoggingIn(true);

      try {
        const loginResult = await loginUser({
          username: loginUsername,
          password: loginPassword,
          rememberMe: true,
        }).unwrap();

        if (loginResult.token) {
          setAuthTokenToCookies(loginResult.token);
          dispatch(userApi.util.resetApiState());
        }

        // ログイン成功 -> 招待承認
        setStatus("accepting");
        try {
          const acceptResult = await acceptInvitation({ token }).unwrap();
          navigate(buildDestination(acceptResult.project_id, acceptResult.offer_id), { replace: true });
        } catch (err) {
          setStatus("error");
          setErrorMessage(extractErrorMessage(err, "プロジェクトへの参加に失敗しました。"));
        }
      } catch (err) {
        setLoginError(
          extractErrorMessage(
            err,
            "サインインに失敗しました。メールアドレスとパスワードをご確認ください。"
          )
        );
      } finally {
        setIsLoggingIn(false);
      }
    },
    [token, loginUsername, loginPassword, loginUser, acceptInvitation, navigate, dispatch, buildDestination]
  );

  // 新規登録完了コールバック
  const handleRegistrationComplete = useCallback(
    (registeredProjectId: string, registeredOfferId?: string) => {
      navigate(buildDestination(registeredProjectId, registeredOfferId), { replace: true });
    },
    [navigate, buildDestination]
  );

  // --- 描画 ---

  // ローディング / 承認処理中 / ログイン済み自動承認待ち
  if (status === "loading" || status === "existing_user_logged_in" || status === "accepting" || status === "done") {
    return (
      <PageContainer>
        <Background>
          <FlowingCircles />
        </Background>
        <SvgLoadingAnimation />
      </PageContainer>
    );
  }

  // 既に承認済み
  if (status === "already_accepted") {
    return (
      <PageContainer>
        <Background>
          <FlowingCircles />
        </Background>
        <Card>
          <StatusMessage>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 48, color: "var(--soremo-blue)", marginBottom: 16, display: "block" }}
            >
              check_circle
            </span>
            <div className="heading-16" style={{ marginBottom: 8 }}>
              {inviteType === "offer" ? "アカウントは作成済みです" : "既に参加済みです"}
            </div>
            <div className="bodytext-13 u-text-light-gray">
              {inviteType === "offer"
                ? "オファースレッドからご確認いただけます。"
                : "この招待は既に承認されています。"}
            </div>
          </StatusMessage>
          <Button
            theme="primary"
            variant="filled"
            size="medium"
            width="full"
            onClick={() =>
              inviteType === "offer" && projectId
                ? navigate(buildDestination(projectId), { replace: true })
                : navigate("/projects", { replace: true })
            }
          >
            {inviteType === "offer" ? "オファーを確認" : "プロジェクト一覧へ"}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // エラー
  if (status === "error") {
    return (
      <PageContainer>
        <Background>
          <FlowingCircles />
        </Background>
        <Card>
          <StatusMessage>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 48, color: "var(--soremo-light-gray)", marginBottom: 16, display: "block" }}
            >
              error_outline
            </span>
            <div className="heading-16" style={{ marginBottom: 8 }}>
              {isExpired ? "招待リンクの有効期限が切れています" : "招待を受け付けられません"}
            </div>
            <div className="bodytext-13 u-text-light-gray">
              {isExpired
                ? "新しいリンクをメールでお送りできます。オファーの内容はそのまま残っています。"
                : errorMessage}
            </div>
            {resendMessage && (
              <div className="bodytext-13" style={{ marginTop: 16 }}>
                {resendMessage}
              </div>
            )}
          </StatusMessage>
          <div className="u-col u-gap8">
            {isExpired && (
              <Button
                theme="primary"
                variant="filled"
                size="medium"
                width="full"
                disabled={isResending}
                onClick={handleResend}
              >
                {isResending ? "送信中..." : "新しいリンクをメールで送る"}
              </Button>
            )}
            <Button
              theme="tertiary"
              variant="filled"
              size="medium"
              width="full"
              onClick={() => navigate("/signin", { replace: true })}
            >
              サインインへ
            </Button>
          </div>
        </Card>
      </PageContainer>
    );
  }

  // 未ログイン既存ユーザー -> サインインフォーム
  if (status === "existing_user_not_logged_in") {
    return (
      <PageContainer>
        <Background>
          <FlowingCircles />
        </Background>
        <Card>
          <div className="u-col u-gap8" style={{ marginBottom: 24 }}>
            <div className="heading-16" style={{ textAlign: "center" }}>
              {inviteType === "offer"
                ? `${senderName || "送信者"}さんからオファーが届いています`
                : "プロジェクトに招待されています"}
            </div>
            {projectName && (
              <div
                className="bodytext-13 u-text-light-gray"
                style={{ textAlign: "center" }}
              >
                {projectName}
              </div>
            )}
            <div
              className="bodytext-13 u-text-light-gray"
              style={{ textAlign: "center" }}
            >
              {inviteType === "offer"
                ? "サインインしてオファーを確認してください"
                : "サインインして参加してください"}
            </div>
          </div>

          <form onSubmit={handleLogin}>
            <div className="u-col u-gap32">
              {loginError && <ErrorMessage>{loginError}</ErrorMessage>}

              <div className="u-w100 u-col u-gap16">
                <Input
                  label=""
                  type="text"
                  placeholder="メールアドレス"
                  option="required"
                  value={loginUsername}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLoginUsername(e.target.value)}
                />
                <Input
                  label=""
                  type="password"
                  placeholder="パスワード"
                  option="required"
                  value={loginPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLoginPassword(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                theme="primary"
                variant="filled"
                size="large"
                width="full"
                rightIcon="chevron_right"
                disabled={isLoggingIn}
              >
                {isLoggingIn ? "サインイン中..." : "サインインして参加"}
              </Button>
            </div>
          </form>
        </Card>
      </PageContainer>
    );
  }

  // 新規ユーザー -> 登録ステップフロー
  if (status === "new_user") {
    // オファー招待（メールアドレスで招待されたクリエイター）は、利用許諾 →
    // パスワード → アカウント → 振込先 → 書類確認（引き受け）の連続オンボーディングへ。
    // 引き受け（取引成立）後に DM（オファースレッド）へ遷移する。
    if (inviteType === "offer" && offerId) {
      return (
        <OfferOnboardingWizard
          token={token}
          email={email}
          projectId={projectId}
          offerId={offerId}
          summary={{
            role: offerRole,
            reward: offerReward,
            deadline: offerDeadline,
          }}
          onComplete={handleRegistrationComplete}
        />
      );
    }
    // プロジェクト招待（クライアント）は従来どおりの 3 ステップ登録。
    return (
      <InviteRegistrationModal
        token={token}
        email={email}
        onComplete={handleRegistrationComplete}
        senderName={inviteType === "offer" ? senderName : undefined}
      />
    );
  }

  return null;
};

export default Invite;
